import React, { useEffect, useMemo, useState } from "react";
import { Col, Modal, Row, Table } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GetData, { fetchFileBlob } from "../../../services/getData";
import EditableModal from "../../../components/layout/editableModal/EditableModal";
import Toast from "../../../components/layout/toast/Toast";
import {
  getUsiTypeOptions,
  getUsiTypeParam,
  isUsiDiagnostics,
} from "./usiFormSchema";
import "../patients.css";
import "./patientInbody.css";
import "./PatientAnalyses.css";

const getGroupOptions = (t) => [
  { value: "laboratory", label: t("diagnostics.groups.laboratory") },
  { value: "instrumental", label: t("diagnostics.groups.instrumental") },
];

const getTypeOptions = (t) => [
  { value: "usi", label: t("diagnostics.types.usi") },
  { value: "inbody_vizbody", label: t("diagnostics.types.inbody_vizbody") },
  { value: "analysis", label: t("diagnostics.types.analysis") },
];

const getAddKindPresets = (t) => ({
  analysis: {
    title: t("diagnostics.addTitles.analysis"),
    group_diagnostics: "laboratory",
    type_diagnostics: "analysis",
  },
  usi: {
    title: t("diagnostics.addTitles.usi"),
    group_diagnostics: "instrumental",
    type_diagnostics: "usi",
  },
});

const getDiagnosticsModalFields = (t) => [
  { name: "name", label: t("diagnostics.fields.name"), type: "text", required: true },
  { name: "date", label: t("diagnostics.fields.date"), type: "date", required: true },
  {
    name: "group_diagnostics",
    label: t("diagnostics.fields.group"),
    type: "select",
    required: true,
    options: getGroupOptions(t),
  },
  {
    name: "type_diagnostics",
    label: t("diagnostics.fields.type"),
    type: "select",
    required: true,
    options: getTypeOptions(t),
  },
  { name: "files", label: t("diagnostics.fields.file"), type: "file", multiple: false, required: true },
];

const getUsiModalFields = (t) => [
  {
    name: "name",
    label: t("diagnostics.fields.usi"),
    type: "select",
    required: true,
    options: getUsiTypeOptions(t),
  },
];

const toIsoDate = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDate = (value) => {
  if (!value) return "—";
  const raw = String(value).trim();
  const isoDate = raw.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return raw;
  const [year, month, day] = isoDate.split("-");
  return `${day}.${month}.${year}`;
};

const getFileExtension = (fileName) => {
  const parts = String(fileName ?? "").split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

const getPrimaryFile = (row) => {
  if (Array.isArray(row?.files) && row.files.length > 0) {
    return row.files[0];
  }

  const fileId = Number(
    Array.isArray(row?.file_ids) && row.file_ids.length > 0
      ? row.file_ids[0]
      : row?.id_files,
  );

  if (fileId > 0) {
    return {
      id: fileId,
      name: String(row?.file_name ?? "").trim(),
    };
  }

  return null;
};

const getDiagnosticsDisplayName = (row) => {
  const userName = String(row?.name ?? "").trim();
  const fileName = String(getPrimaryFile(row)?.name ?? "").trim();

  if (userName && fileName) {
    return `${userName} (${fileName})`;
  }
  if (userName) return userName;
  if (fileName) return fileName;
  return "—";
};

function DiagnosticsFilePreview({ file }) {
  const { t } = useTranslation("patient");
  const [blobUrl, setBlobUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fileId = Number(file?.id);
    if (!fileId) {
      setLoading(false);
      setError(t("diagnostics.preview.noFile"));
      return undefined;
    }

    let cancelled = false;
    let objectUrl = "";

    (async () => {
      setLoading(true);
      setError("");
      try {
        const blob = await fetchFileBlob(fileId, true);
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) {
          setBlobUrl(objectUrl);
        }
      } catch (fetchError) {
        if (!cancelled) {
          setError(fetchError?.message || t("diagnostics.preview.loadFailed"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [file?.id, t]);

  if (loading) {
    return <div className="analysis-view-file__state">{t("diagnostics.preview.loading")}</div>;
  }

  if (error) {
    return <div className="analysis-view-file__state analysis-view-file__state--error">{error}</div>;
  }

  const ext = getFileExtension(file?.name);

  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
    return (
      <div className="analysis-view-file">
        <img src={blobUrl} alt={file?.name || t("diagnostics.preview.fileAlt")} className="analysis-view-file__image" />
      </div>
    );
  }

  if (ext === "pdf") {
    return (
      <div className="analysis-view-file">
        <iframe
          src={blobUrl}
          title={file?.name || t("diagnostics.preview.fileAlt")}
          className="analysis-view-file__frame"
        />
      </div>
    );
  }

  return (
    <div className="analysis-view-file">
      <div className="analysis-view-file__state">{t("diagnostics.preview.unsupported")}</div>
    </div>
  );
}

function PatientDiagnostics() {
  const { t } = useTranslation("patient");
  const navigate = useNavigate();
  const { patientId } = useParams();
  const id_patient = Number(patientId);

  const addKindPresets = useMemo(() => getAddKindPresets(t), [t]);
  const diagnosticsModalFields = useMemo(() => getDiagnosticsModalFields(t), [t]);
  const usiModalFields = useMemo(() => getUsiModalFields(t), [t]);

  const [diagnostics, setDiagnostics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addKind, setAddKind] = useState("analysis");
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState(false);
  const [selectedDiagnostics, setSelectedDiagnostics] = useState(null);
  const [toast, setToast] = useState({});

  const addPreset = addKindPresets[addKind] || addKindPresets.analysis;
  const addModalFields = addKind === "usi" ? usiModalFields : diagnosticsModalFields;

  const initialData = useMemo(
    () => ({
      name: "",
      date: toIsoDate(new Date()),
      group_diagnostics: addPreset.group_diagnostics,
      type_diagnostics: addPreset.type_diagnostics,
      files: [],
    }),
    [addPreset],
  );

  const openAddModal = (kind) => {
    setAddKind(kind);
    setShowAddModal(true);
  };

  const showToast = (title, status = "success") => {
    setToast({
      id: Date.now(),
      title,
      status,
    });
  };

  const fetchDiagnostics = async () => {
    if (!id_patient) {
      setDiagnostics([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "diagnostics",
        id_patient,
      });

      if (Array.isArray(data?.result)) {
        setDiagnostics(data.result);
      } else if (data?.result) {
        setDiagnostics([data.result]);
      } else {
        setDiagnostics([]);
      }
    } catch (error) {
      console.error("Ошибка загрузки диагностик:", error);
      setDiagnostics([]);
      showToast(t("diagnostics.toast.loadFailed"), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDiagnostics();
  }, [id_patient]);

  const handleSaveDiagnostics = async (formData) => {
    const name = String(formData?.name ?? "").trim();
    const group_diagnostics = String(
      formData?.group_diagnostics ?? addPreset.group_diagnostics ?? "",
    ).trim();
    const type_diagnostics = String(
      formData?.type_diagnostics ?? addPreset.type_diagnostics ?? "",
    ).trim();
    const date = formData?.date || toIsoDate(new Date());
    const files = Array.isArray(formData?.files) ? formData.files : [];

    if (!name) {
      showToast(
        addKind === "usi"
          ? t("diagnostics.toast.selectUsi")
          : t("diagnostics.toast.enterName"),
        "error",
      );
      return false;
    }
    if (addKind === "usi") {
      try {
        const response = await GetData({
          module: "patient",
          block: "diagnostics",
          action: "add",
          id_patient,
          name,
          date,
          group_diagnostics: addPreset.group_diagnostics,
          type_diagnostics: "usi",
        });

        if (response?.status?.success === false) {
          showToast(
            response?.status?.message || t("diagnostics.toast.addUsiFailed"),
            "error",
          );
          return false;
        }

        const createdId = Number(response?.created_id);
        setShowAddModal(false);
        if (createdId > 0) {
          navigate(
            `/patient/${id_patient}/diagnostics/uzi/${createdId}?type=${encodeURIComponent(
              getUsiTypeParam(name),
            )}`,
          );
        } else {
          showToast(t("diagnostics.toast.usiAddedNoProtocol"), "error");
          fetchDiagnostics();
        }
        return true;
      } catch (error) {
        console.error("Ошибка добавления УЗИ:", error);
        showToast(t("diagnostics.toast.addUsiFailed"), "error");
        return false;
      }
    }
    if (!group_diagnostics) {
      showToast(t("diagnostics.toast.selectGroup"), "error");
      return false;
    }
    if (!type_diagnostics) {
      showToast(t("diagnostics.toast.selectType"), "error");
      return false;
    }
    if (!files.length) {
      showToast(t("diagnostics.toast.attachFile"), "error");
      return false;
    }

    try {
      const response = await GetData(
        {
          module: "patient",
          block: "diagnostics",
          action: "add",
          id_patient,
          name,
          date,
          group_diagnostics,
          type_diagnostics,
        },
        files,
      );

      if (response?.status?.success === false) {
        showToast(
          response?.status?.message || t("diagnostics.toast.addFailed"),
          "error",
        );
        return false;
      }

      showToast(response?.status?.message || t("diagnostics.toast.added"), "success");
      setShowAddModal(false);
      fetchDiagnostics();
      return true;
    } catch (error) {
      console.error("Ошибка добавления диагностики:", error);
      showToast(t("diagnostics.toast.addFailed"), "error");
      return false;
    }
  };

  const openDiagnosticsRow = (row) => {
    if (isUsiDiagnostics(row)) {
      if (!row?.id) return;
      const typeParam = getUsiTypeParam(row?.name);
      const params = new URLSearchParams();
      if (typeParam) params.set("type", typeParam);
      // Из списка протокол сначала открывается на просмотр,
      // редактирование включается кнопкой «Редактировать» внутри протокола.
      params.set("mode", "view");
      navigate(
        `/patient/${id_patient}/diagnostics/uzi/${row.id}?${params.toString()}`,
      );
      return;
    }
    handleViewDiagnostics(row);
  };

  const handleViewDiagnostics = async (row) => {
    if (!row?.id) return;

    setShowViewModal(true);
    setViewLoading(true);
    setSelectedDiagnostics(row);

    try {
      const data = await GetData({
        module: "patient",
        block: "diagnostics",
        action: "view",
        id_patient,
        id: row.id,
      });

      if (data?.status?.success === false) {
        showToast(data?.status?.message || t("diagnostics.toast.viewFailed"), "error");
        return;
      }

      if (data?.result) {
        setSelectedDiagnostics(data.result);
      }
    } catch (error) {
      console.error("Ошибка загрузки диагностики:", error);
      showToast(t("diagnostics.toast.viewFailed"), "error");
    } finally {
      setViewLoading(false);
    }
  };

  const handleDownloadFile = async (file) => {
    const fileId = Number(file?.id);
    if (!fileId || downloadingFile) return;

    setDownloadingFile(true);
    try {
      const blob = await fetchFileBlob(fileId, false);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = file?.name || "file";
      link.click();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Ошибка скачивания файла:", error);
      showToast(error?.message || t("diagnostics.toast.downloadFailed"), "error");
    } finally {
      setDownloadingFile(false);
    }
  };

  const primaryFile = getPrimaryFile(selectedDiagnostics);

  return (
    <div className="patient-dynamics patient-analyses">
      {toast?.title ? (
        <Toast
          key={toast.id}
          title={toast.title}
          status={toast.status}
          delay={3500}
        />
      ) : null}

      {loading ? (
        <div className="loading">{t("state.loading", { ns: "common" })}</div>
      ) : diagnostics.length ? (
        <Row>
          <Col>
            <div className="table-list">
              <Table className="main-table-list rounded-table">
                <thead>
                  <tr>
                    <th>{t("diagnostics.table.name")}</th>
                    <th>{t("diagnostics.table.createdAt")}</th>
                  </tr>
                </thead>
                <tbody>
                  {diagnostics.map((row) => {
                    const displayName = getDiagnosticsDisplayName(row);
                    const canView = Boolean(row?.id) || isUsiDiagnostics(row);

                    return (
                      <tr key={row?.id ?? `${row?.created_date}-${row?.name}`}>
                        <td>
                          {canView ? (
                            <button
                              type="button"
                              className="table-item-link analysis-list-table__name-link"
                              onClick={() => openDiagnosticsRow(row)}
                              title={t("diagnostics.table.viewTitle")}
                            >
                              <div className="table-item-name">{displayName}</div>
                            </button>
                          ) : (
                            <div className="table-item-name">{displayName}</div>
                          )}
                        </td>
                        <td>{formatDate(row?.created_date)}</td>
                      </tr>
                    );
                  })}
                  <tr className="table-summary">
                    <td colSpan={2} className="summary-cell">
                      {t("diagnostics.table.found", { count: diagnostics.length })}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>
      ) : (
        <div className="no-patients">{t("diagnostics.table.empty")}</div>
      )}

      {!loading ? (
        <div className="patient-list-add">
          <button
            type="button"
            className="button-default"
            onClick={() => openAddModal("analysis")}
          >
            {t("diagnostics.buttons.addAnalysis")}
          </button>
          <button
            type="button"
            className="button-default light"
            onClick={() => openAddModal("usi")}
          >
            {t("diagnostics.buttons.addUsi")}
          </button>
        </div>
      ) : null}

      <EditableModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        title={addPreset.title}
        initialData={initialData}
        fields={addModalFields}
        onSave={handleSaveDiagnostics}
        closeOnSave={false}
      />

      <Modal
        show={showViewModal}
        onHide={() => {
          setShowViewModal(false);
          setSelectedDiagnostics(null);
        }}
        size="lg"
        centered
        className="analysis-view-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedDiagnostics?.name || t("diagnostics.view.fallbackTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewLoading && !selectedDiagnostics ? (
            <div className="analysis-view-empty">{t("diagnostics.view.loading")}</div>
          ) : selectedDiagnostics ? (
            <div className="analysis-view-content">
              <section className="analysis-view-section">
                <h3 className="analysis-view-section__title">{t("diagnostics.view.date")}</h3>
                <div className="analysis-view-section__body">
                  {formatDate(selectedDiagnostics?.date)}
                </div>
              </section>

              <section className="analysis-view-section">
                <h3 className="analysis-view-section__title">{t("diagnostics.view.group")}</h3>
                <div className="analysis-view-section__body">
                  {selectedDiagnostics?.group_diagnostics
                    ? t(`diagnostics.groups.${selectedDiagnostics.group_diagnostics}`, {
                        defaultValue: selectedDiagnostics.group_diagnostics,
                      })
                    : "—"}
                </div>
              </section>

              <section className="analysis-view-section">
                <h3 className="analysis-view-section__title">{t("diagnostics.view.type")}</h3>
                <div className="analysis-view-section__body">
                  {selectedDiagnostics?.type_diagnostics || "—"}
                </div>
              </section>

              <section className="analysis-view-section">
                <h3 className="analysis-view-section__title">{t("diagnostics.view.file")}</h3>
                {primaryFile ? (
                  <DiagnosticsFilePreview file={primaryFile} />
                ) : (
                  <div className="analysis-view-empty">{t("diagnostics.preview.noFile")}</div>
                )}
              </section>
            </div>
          ) : (
            <div className="analysis-view-empty">{t("diagnostics.view.notFound")}</div>
          )}
        </Modal.Body>
        {selectedDiagnostics ? (
          <Modal.Footer className="analysis-view-modal__footer">
            <button
              type="button"
              className="button-default light"
              onClick={() => handleDownloadFile(primaryFile)}
              disabled={!primaryFile || downloadingFile}
            >
              {downloadingFile ? t("diagnostics.view.downloading") : t("diagnostics.view.download")}
            </button>
          </Modal.Footer>
        ) : null}
      </Modal>
    </div>
  );
}

export default PatientDiagnostics;
